import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuCar, LuMapPin, LuCircleCheck, LuClock, LuArrowRight } from 'react-icons/lu';
import api from '../../services/api';
import ModalShell from '../ui/ModalShell';
import DireccionMapa from '../ui/DireccionMapa';
import ChatVisita from './ChatVisita';
import AntesDeIr from './AntesDeIr';
import { EtiquetaTentativa, RespuestaTentativa } from './FechaTentativa';
import { fechaAR, getTodayISO } from '../../utils/dateUtils';
import { horaTexto } from '../../utils/ordenes';

// Ficha de una visita para el técnico, al tocarla en su agenda (12-oct-2026).
// Antes la tarjeta de la agenda no abría nada: no había forma de ver el paso a
// paso (asignada, ok voy, salí, llegué, terminé) ni de avanzarlo desde ahí.

const ORDEN_PASO = { PENDIENTE: 0, EN_CAMINO: 2, EN_SITIO: 3, COMPLETADA: 4 };
const PASOS = [
    { label: 'Asignada',  busca: 'Visita asignada' },
    { label: 'Ok, voy',   busca: 'Confirmó' },
    { label: 'Salí',      busca: 'Salió' },
    { label: 'Llegué',    busca: 'Llegó' },
    { label: 'Terminada', busca: 'Terminó' },
];
const SIGUIENTE = {
    PENDIENTE: { estado: 'EN_CAMINO', label: 'Salí', Icon: LuCar },
    EN_CAMINO: { estado: 'EN_SITIO', label: 'Llegué', Icon: LuMapPin },
};

const horaDe = (iso) => {
    try { return new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
};

export default function FichaVisitaTecnico({ orden: inicial, onCerrar, onCambio }) {
    const [orden, setOrden] = useState(inicial);
    const [mensajes, setMensajes] = useState([]);
    const [moviendo, setMoviendo] = useState(false);

    const cargarMensajes = () => api.get(`/ordenes/${orden.id}/mensajes`)
        .then(r => setMensajes(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { cargarMensajes(); }, [orden.id]);

    const tentativa = !!orden.sinConfirmar && orden.estado === 'PENDIENTE';
    const abierta = ['PENDIENTE', 'EN_CAMINO', 'EN_SITIO'].includes(orden.estado);
    const noSeHizo = orden.estado === 'NO_ATENDIDO' || orden.estado === 'CANCELADA';

    // Hasta qué paso llegó: el estado manda; "Ok, voy" sale de confirmadaEn
    const hecho = (i) => {
        if (i === 0) return true;
        if (i === 1) return !!orden.confirmadaEn || (ORDEN_PASO[orden.estado] ?? 0) >= 2;
        return (ORDEN_PASO[orden.estado] ?? 0) >= i;
    };
    // La hora de cada paso, del historial de la visita (el último registro de cada uno)
    const cuando = (paso) => {
        const m = [...mensajes].reverse().find(x => x.sistema && String(x.texto || '').startsWith(paso.busca));
        return m ? horaDe(m.creadoEn) : '';
    };

    const avanzar = async (estado) => {
        setMoviendo(true);
        try {
            const r = await api.patch(`/ordenes/${orden.id}/estado`, { estado });
            setOrden(o => ({ ...o, ...(r.data || { estado }) }));
            cargarMensajes(); onCambio?.();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo actualizar la visita');
        } finally { setMoviendo(false); }
    };
    const okVoy = async () => {
        setMoviendo(true);
        try {
            const r = await api.patch(`/ordenes/${orden.id}/confirmar`);
            setOrden(o => ({ ...o, ...r.data }));
            toast.success('Listo, el admin ya sabe que vas');
            cargarMensajes(); onCambio?.();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo confirmar');
        } finally { setMoviendo(false); }
    };
    // Cerrar el trabajo pide fotos, ítems y cobro: eso está en "Hoy"
    const irAHoy = () => {
        window.dispatchEvent(new CustomEvent('ir-a-visita', { detail: String(orden.id) }));
        onCerrar();
    };

    const sig = SIGUIENTE[orden.estado];
    const fechaOk = !orden.fechaProgramada || orden.fechaProgramada >= getTodayISO();
    const pideOk = orden.estado === 'PENDIENTE' && !orden.confirmadaEn && fechaOk;
    const esDelDia = !orden.fechaProgramada || orden.fechaProgramada <= getTodayISO();
    const hora = horaTexto(orden.horaEstimada) || 'Sin horario';
    const fila = 'flex items-center justify-between gap-3 py-2.5 border-b border-black/[0.06] dark:border-white/[0.06] last:border-0';

    let accion = null;
    if (abierta && !tentativa) {
        if (pideOk) accion = { label: '✓ Ok, voy', fn: okVoy, color: 'var(--etapa-listo)' };
        // 31-oct-2026: "Salí" solo el día de la visita (o atrasada), igual que en Hoy
        else if (sig && (orden.estado !== 'PENDIENTE' || esDelDia)) accion = { label: <><sig.Icon size={15} /> {sig.label}</>, fn: () => avanzar(sig.estado), color: 'var(--etapa-marcha)' };
        else if (orden.estado === 'EN_SITIO') accion = { label: <><LuCircleCheck size={15} /> Cerrar trabajo <LuArrowRight size={14} /></>, fn: irAHoy, color: 'var(--etapa-listo)' };
    }

    return (
        <ModalShell titulo={orden.clienteNombre || orden.titulo || `Visita #${orden.id}`}
            subtitulo={orden.titulo && orden.clienteNombre && !orden.titulo.includes(orden.clienteNombre) ? orden.titulo : undefined}
            onCerrar={onCerrar}
            pie={
                <div className="flex gap-2">
                    <button type="button" onClick={onCerrar}
                        className="flex-1 h-11 rounded-xl bg-chip text-secondary text-label font-black active:scale-95">Cerrar</button>
                    {!accion && abierta && !tentativa && orden.estado === 'PENDIENTE' && !esDelDia && (
                        <p className="flex-[2] self-center text-caption text-muted text-center">"Salí" se habilita el día de la visita</p>
                    )}
                    {accion && (
                        <button type="button" disabled={moviendo} onClick={accion.fn} style={{ background: accion.color }}
                            className="flex-[2] h-11 rounded-xl text-white text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-40">
                            {accion.label}
                        </button>
                    )}
                </div>
            }>
            <div className="space-y-4">
                <div className="rounded-2xl bg-panel px-4">
                    <div className={fila}>
                        <span className="text-caption text-muted">Cuándo</span>
                        <span className="flex items-center gap-1.5 text-body font-bold text-ink"><LuClock size={14} />{fechaAR(orden.fechaProgramada)} · {hora}</span>
                    </div>
                    {orden.direccion && (
                        <div className="py-2.5 border-b border-black/[0.06] dark:border-white/[0.06] last:border-0">
                            <DireccionMapa direccion={orden.direccion} />
                        </div>
                    )}
                    {orden.equiposSerie && (
                        <div className="py-2.5 flex flex-wrap gap-1 border-b border-black/[0.06] dark:border-white/[0.06] last:border-0">
                            {orden.equiposSerie.split(',').filter(Boolean).map(ns => (
                                <span key={ns} className="px-2 py-0.5 rounded-md bg-chip text-label font-black text-ink">{ns}</span>
                            ))}
                        </div>
                    )}
                    {orden.descripcion && <p className="py-2.5 text-caption text-secondary whitespace-pre-line">{orden.descripcion}</p>}
                </div>

                <AntesDeIr ordenId={orden.id} hecha={orden.estado === 'COMPLETADA'} />

                {tentativa && (
                    <div>
                        <EtiquetaTentativa />
                        <RespuestaTentativa orden={orden} onCambio={() => {
                            api.get(`/ordenes/${orden.id}`).then(r => setOrden(r.data)).catch(() => {});
                            onCambio?.();
                        }} />
                    </div>
                )}

                {/* Paso a paso */}
                <div>
                    <p className="text-label font-black text-muted uppercase tracking-widest mb-2">Paso a paso</p>
                    <ol className="rounded-2xl bg-panel px-4 py-1">
                        {PASOS.map((p, i) => {
                            const ok = hecho(i);
                            const t = ok ? cuando(p) : '';
                            return (
                                <li key={p.label} className="flex items-center gap-3 py-2">
                                    <span className={`w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-label font-black ${ok ? 'text-white' : 'bg-chip text-muted'}`}
                                        style={ok ? { background: 'var(--etapa-listo)' } : undefined}>
                                        {ok ? '✓' : i + 1}
                                    </span>
                                    <span className={`flex-1 text-body font-bold ${ok ? 'text-ink' : 'text-muted'}`}>{p.label}</span>
                                    {t && <span className="text-caption text-muted">{t}</span>}
                                </li>
                            );
                        })}
                        {noSeHizo && (
                            <li className="flex items-center gap-3 py-2">
                                <span className="w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-label font-black text-white bg-[var(--alerta,#DC2626)]">!</span>
                                <span className="flex-1 text-body font-bold text-ink">{orden.estado === 'CANCELADA' ? 'Cancelada por el admin' : 'No se pudo hacer'}</span>
                            </li>
                        )}
                    </ol>
                    {orden.notasTecnico && <p className="mt-2 text-caption text-secondary">Nota: {orden.notasTecnico}</p>}
                    {abierta && !tentativa && (
                        <button type="button" onClick={irAHoy} className="mt-2 text-caption font-bold text-muted underline active:opacity-60">
                            Ver en "Hoy" (contactar al cliente, avisar un problema…)
                        </button>
                    )}
                </div>

                <ChatVisita orden={orden} soyAdmin={false} />
            </div>
        </ModalShell>
    );
}
